import React from "react";
import { ChevronDown, House } from "lucide-react";
import {
  EmptyState,
  HoverCard,
  PanelShell,
  DISPATCH_DARK_HEADER_CHIP_CLASS_NAME,
  DISPATCH_DARK_HEADER_CLASS_NAME,
  DISPATCH_FRAME_RADIUS_CLASS_NAME,
  DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
  joinClasses,
} from "./ResultsPrimitives.jsx";
import RouteMetaChipRow, { getRouteMetaChipDisplayLabel } from "./RouteMetaChipRow.jsx";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { readCurrentDispatchDate } from "../utils/dispatchDateUrls.js";
import {
  applyFleetSegmentComparisonDurations,
  buildFleetDisplayedSegmentTimings,
  buildFleetSegmentComparisonCacheKey,
  buildFleetSequenceDisplayItems,
  buildSameSiteSequenceDisplayOverrides,
  buildRouteSequenceRenderItems,
  formatTimelineClockLabel,
  getRouteSequenceDisplaySummaryLabel,
  getRouteSequencePrimaryAddress,
  getSameSiteSequenceDisplayDistanceLabel,
  resolveAspiratorMissionRouteSegmentIndexes,
  resolveGroupedHoverRouteSegmentIndex,
  resolveSameSiteSequenceDisplayedTravelDuration,
  isInitialRouteSequenceTravelSegment,
  isPreviousDayPreloadDisplaySegment,
  getRouteSequencePrimaryTitle,
  isMergedOperationalMissionSegment,
  joinGroupedSegmentCompartmentLabel,
  shouldSyncDisplayedTravelDurationChip,
} from "../utils/fleetTimelinePresentation.js";
import { formatDurationShort, formatManualBufferReasonLabel } from "../utils/routeModelsData.js";
import { formatTons } from "../utils/resultsFleetData.js";
import { applyCompartmentOccupancyChange, cloneCompartmentOccupancy } from "../utils/compartmentOccupancy.js";
import { getFleetProductTone } from "../utils/cropColors.js";
import {
  buildGroupedRouteSequenceDistanceHoverCard,
  buildGroupedRouteSequenceDurationHoverCard,
  buildRouteSequenceDistanceHoverCard,
  buildRouteSequenceDisplayedTravelTitle,
  buildRouteSequenceDurationHoverCard,
  buildRouteSequenceOpeningIndicator,
  buildRouteSequenceOpeningVisibilityByItemKey,
  filterRouteSequenceOpeningIndicators,
  formatRouteSequenceDurationChipLabel,
  formatRouteSequenceTravelDurationLabel,
  formatSequenceGroupDistanceLabel,
  getGroupedSegmentSequenceLabel,
  getRouteSequenceGroupEndValue,
  getRouteSequenceGroupStartValue,
  resolveRouteSequenceDisplayedTravelLabel,
  shouldDisplayRouteSequenceOpeningForItem,
} from "../utils/routeSequenceHoverCards.js";

const stopBadgeClassNames = {
  delivery: "border-[var(--dispatch-sequence-badge-delivery-border)] bg-[var(--dispatch-sequence-badge-delivery-fill)] text-[var(--route-delivery-strong)]",
  depot_return: "border-[var(--dispatch-sequence-badge-return-border)] bg-[var(--dispatch-sequence-badge-return-fill)] text-accent-strong",
  depot_start: "border-[var(--dispatch-sequence-badge-return-border)] bg-[var(--dispatch-sequence-badge-return-fill)] text-accent-strong",
  loading: "border-[var(--dispatch-sequence-badge-loading-border)] bg-[var(--dispatch-sequence-badge-loading-fill)] text-[var(--route-loading-strong)]",
};

const stopIconClassNames = {
  delivery: "rounded-full border-[1.25px] border-[var(--dispatch-sequence-icon-delivery-border)] bg-transparent text-[var(--route-delivery-strong)] shadow-none",
  depot_return: "border border-white/40 bg-[radial-gradient(circle_at_32%_28%,rgba(255,255,255,0.34),transparent_38%),linear-gradient(180deg,var(--dispatch-sequence-icon-return-mid,#3f8e87)_0%,var(--dispatch-sequence-return-marker-fill,var(--color-legacy-teal))_100%)] text-white shadow-[0_16px_30px_rgba(15,92,87,0.22)]",
  depot_start: "border border-white/40 bg-[radial-gradient(circle_at_32%_28%,rgba(255,255,255,0.34),transparent_38%),linear-gradient(180deg,var(--dispatch-sequence-icon-return-mid,#3f8e87)_0%,var(--dispatch-sequence-return-marker-fill,var(--color-legacy-teal))_100%)] text-white shadow-[0_16px_30px_rgba(15,92,87,0.22)]",
  loading: "rounded-full border-[1.25px] border-[var(--dispatch-sequence-icon-loading-border)] bg-transparent text-[var(--route-loading-strong)] shadow-none",
};

const routeStartMarkerClassName = "inline-flex h-6.5 w-6.5 items-center justify-center rounded-full border border-white/40 bg-[radial-gradient(circle_at_32%_28%,rgba(255,255,255,0.34),transparent_38%),linear-gradient(180deg,var(--dispatch-sequence-icon-return-mid,#3f8e87)_0%,var(--dispatch-sequence-return-marker-fill,var(--color-legacy-teal))_100%)] text-white shadow-[0_10px_20px_rgba(15,92,87,0.2),0_0_0_2px_rgba(255,255,255,0.88)]";
const ROUTE_SEQUENCE_PRODUCT_CHIP_MAX_LENGTH = 12;
// Le nom de l'arrêt garde une largeur minimale ; les pastilles prennent la largeur de leurs colonnes.
const ROUTE_SEQUENCE_DETAILS_GRID_CLASS_NAME = "lg:grid-cols-[minmax(10rem,1fr)_6.9rem_auto]";
const ROUTE_SEQUENCE_CHIP_GRID_CLASS_NAME = "lg:grid lg:grid-cols-(--route-chip-columns) lg:items-center lg:gap-x-1 lg:gap-y-0";
// Meunerie, Aspir. et Tampon n'ont de largeur que si la tournée en contient.
const ROUTE_SEQUENCE_CHIP_COLUMNS = [
  { kind: "compartment", label: "Compartiments", width: "8.4rem" },
  { kind: "occupancy", label: "Occ.", width: "3.2rem" },
  { kind: "grain", label: "Code", width: "5rem" },
  { kind: "qty", label: "Tonnage", width: "4.4rem" },
  { kind: "dist", label: "Dist.", width: "4.4rem" },
  { kind: "svc", label: "Service", width: "4.2rem" },
  { kind: "meunerie", label: "Meunerie", width: "5.2rem", optional: true },
  { kind: "aspirator_time", label: "Aspir.", width: "5.2rem", optional: true },
  { kind: "manual_buffer", label: "Tampon", width: "5.2rem", optional: true },
  { kind: "dur", label: "Trajet", width: "5.4rem" },
];
const ROUTE_SEQUENCE_CHIP_COLUMN_CLASS_NAME_MAP = {
  compartment: "lg:col-start-1",
  occupancy: "lg:col-start-2",
  grain: "lg:col-start-3",
  qty: "lg:col-start-4",
  dist: "lg:col-start-5",
  svc: "lg:col-start-6",
  meunerie: "lg:col-start-7",
  aspirator_time: "lg:col-start-8",
  manual_buffer: "lg:col-start-9",
  dur: "lg:col-start-10",
};
const ROUTE_SEQUENCE_SUBGROUP_HIDDEN_CHIP_KINDS = new Set(["dist", "svc", "meunerie", "aspirator_time", "dur"]);

const ROUTE_SEQUENCE_PRIORITY_SORT_WEIGHT = {
  P1: 3,
  P2: 2,
  P3: 1,
};

const ROUTE_SEQUENCE_INDICATOR_TONE_CLASS_NAME_MAP = Object.freeze({
  active: "border-[rgba(31,111,75,0.28)] bg-[rgba(218,243,229,0.94)] text-[rgb(31,111,75)]",
  aspiration: "border-[rgba(234,88,12,0.22)] bg-[rgba(255,247,237,0.98)] text-[rgb(194,65,12)]",
  neutral: "border-[rgba(88,106,145,0.18)] bg-[rgba(246,248,252,0.94)] text-[rgb(84,100,137)]",
  opening: "border-[rgba(37,99,235,0.22)] bg-white text-[rgb(29,78,216)]",
  outline: "border-[rgba(88,106,145,0.18)] bg-[rgba(246,248,252,0.94)] text-[rgb(84,100,137)]",
  "product-change": "border-[rgba(13,148,136,0.32)] bg-[rgba(204,251,241,0.98)] text-[rgb(15,118,110)] shadow-[0_2px_6px_rgba(13,148,136,0.16)]",
  schedule: "border-[rgba(148,31,63,0.22)] bg-[rgba(252,234,239,0.96)] text-[rgb(148,31,63)]",
  warning: "border-[rgba(31,111,75,0.28)] bg-[rgba(218,243,229,0.94)] text-[rgb(31,111,75)]",
});

const INITIAL_PRELOAD_ROUTE_SEQUENCE_DIAGONAL_LABEL = "Veille";
const INITIAL_PRELOAD_ROUTE_SEQUENCE_PILL_LABEL = "Chargé la veille";
const INITIAL_PRELOAD_ROUTE_SEQUENCE_TITLE = "Chargement réalisé la veille, hors plage chauffeur.";
const END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_DIAGONAL_LABEL = "PM";
const END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_PILL_LABEL = "Chargé en PM";
const END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_TITLE = "Chargement PM réalisé hors plage chauffeur.";

function resolveRouteSequenceSegmentComparisonErrorMessage(payload, fallbackMessage = "Impossible de charger le comparatif du segment.") {
  const message = typeof payload?.message === "string" ? payload.message.trim() : "";
  return message || fallbackMessage;
}

async function fetchRouteSequenceSegmentComparison({ dispatchDate = "", routeSegmentIndex, signal, tripId }) {
  const searchParams = new URLSearchParams({
    trip_id: tripId,
    route_segment_index: String(routeSegmentIndex),
  });
  const resolvedDispatchDate = typeof dispatchDate === "string" && dispatchDate.trim()
    ? dispatchDate.trim()
    : readCurrentDispatchDate();

  if (resolvedDispatchDate) {
    searchParams.set("dispatch_date", resolvedDispatchDate);
  }

  const response = await fetchWithSupabaseAuth(`/api/results/latest/segment-comparison?${searchParams.toString()}`, { signal });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(resolveRouteSequenceSegmentComparisonErrorMessage(payload));
  }

  return payload;
}

function resolveRouteSequenceMeunerieClientKey(item) {
  return (item?.loading_client_name || item?.client_name || item?.clientName || item?.order_id || item?.orderLabel || "").trim();
}

function resolveRouteSequenceMeunerieExtraMinutes(item) {
  if (!item || item?.isPreloadedPreviousDay || item?.is_preloaded_previous_day) {
    return 0;
  }

  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";
  if (stopType !== "loading") {
    return 0;
  }

  const directExtraMinutes = Number(item?.meunerie_loading_extra_minutes);
  if (Number.isFinite(directExtraMinutes) && directExtraMinutes > 0) {
    return Math.round(directExtraMinutes);
  }

  const meunerieChip = (Array.isArray(item?.chips) ? item.chips : []).find(
    (chip) => chip?.kind === "meunerie" || chip?.key === "meunerie",
  );
  if (typeof meunerieChip?.label === "string") {
    const parsedExtraMinutes = Number(meunerieChip.label.replace(/[^\d]/g, ""));
    if (Number.isFinite(parsedExtraMinutes) && parsedExtraMinutes > 0) {
      return Math.round(parsedExtraMinutes);
    }
  }

  return 0;
}

function resolveRouteSequenceGroupMeunerieExtraMinutes(memberSegments = []) {
  const seenClients = new Set();
  let totalMeunerieExtra = 0;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    const extraMinutes = resolveRouteSequenceMeunerieExtraMinutes(segment);
    if (extraMinutes <= 0) {
      return;
    }

    const clientKey = resolveRouteSequenceMeunerieClientKey(segment);
    if (clientKey && seenClients.has(clientKey)) {
      return;
    }

    if (clientKey) {
      seenClients.add(clientKey);
    }

    totalMeunerieExtra += extraMinutes;
  });

  return totalMeunerieExtra;
}

function buildRouteSequenceMeunerieChip(extraMinutes, keyPrefix = "segment") {
  const normalizedExtraMinutes = Math.max(0, Math.round(Number(extraMinutes) || 0));
  if (normalizedExtraMinutes <= 0) {
    return null;
  }

  return {
    key: `${keyPrefix}:meunerie`,
    kind: "meunerie",
    label: `+${normalizedExtraMinutes} min`,
    tone: "amber",
    hoverCardTitle: "Surtemps meunerie",
    title: `Surtemps meunerie : +${normalizedExtraMinutes} min. Il se règle dans Data Manager > Clients. Il s'ajoute au temps de chargement calculé à partir du tonnage et du débit camion.`,
  };
}

function resolveRouteSequenceAspiratorServiceKey(item) {
  return (item?.operationOrderId || item?.orderLabel || item?.order_id || item?.operationDeliveryId || item?.delivery_id || item?.key || "").trim();
}

function resolveRouteSequenceAspiratorServiceMinutes(item) {
  if (!item) {
    return 0;
  }

  if (item?.isAspiratorMissionMerged === true) {
    const missionMinutes = Number(item?.aspiratorServiceMinutes ?? item?.aspirator_service_minutes);
    return Number.isFinite(missionMinutes) && missionMinutes > 0 ? Math.round(missionMinutes) : 0;
  }

  if (isMergedOperationalMissionSegment(item) && Array.isArray(item?.mergedMemberSegments)) {
    return resolveRouteSequenceGroupAspiratorServiceMinutes(item.mergedMemberSegments);
  }

  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";
  if (stopType !== "delivery") {
    return 0;
  }

  const directMinutes = Number(item?.aspiratorServiceMinutes ?? item?.aspirator_service_minutes);
  if (Number.isFinite(directMinutes) && directMinutes > 0) {
    return Math.round(directMinutes);
  }

  const aspiratorChip = (Array.isArray(item?.chips) ? item.chips : []).find(
    (chip) => chip?.kind === "aspirator_time" || chip?.key === "aspirator_time",
  );
  if (typeof aspiratorChip?.label === "string") {
    const parsedMinutes = Number(aspiratorChip.label.replace(/[^\d]/g, ""));
    if (Number.isFinite(parsedMinutes) && parsedMinutes > 0) {
      return Math.round(parsedMinutes);
    }
  }

  return 0;
}

function resolveRouteSequenceGroupAspiratorServiceMinutes(memberSegments = []) {
  const seenKeys = new Set();
  let totalMinutes = 0;

  (Array.isArray(memberSegments) ? memberSegments : []).forEach((segment) => {
    const minutes = resolveRouteSequenceAspiratorServiceMinutes(segment);
    if (minutes <= 0) {
      return;
    }

    const serviceKey = resolveRouteSequenceAspiratorServiceKey(segment);
    if (serviceKey && seenKeys.has(serviceKey)) {
      return;
    }

    if (serviceKey) {
      seenKeys.add(serviceKey);
    }

    totalMinutes += minutes;
  });

  return totalMinutes;
}

function buildRouteSequenceAspiratorTimeChip(extraMinutes, keyPrefix = "segment") {
  const normalizedExtraMinutes = Math.max(0, Math.round(Number(extraMinutes) || 0));
  if (normalizedExtraMinutes <= 0) {
    return null;
  }

  return {
    key: `${keyPrefix}:aspirator_time`,
    kind: "aspirator_time",
    label: `+${normalizedExtraMinutes} min`,
    tone: "aspiration",
    hoverCardTitle: "Temps aspirateur",
    title: `Temps aspirateur : +${normalizedExtraMinutes} min. Temps du camion (la base) plus les tonnes au débit d'aspiration (Paramètres système), quand la commande exige l'aspirateur.`,
  };
}

const MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE = "Délai inter-produit mix produit";
// Pause dîner (Paramètres système) : posée comme un délai, nommée comme telle.
const LUNCH_BREAK_BUFFER_NOTE = "Pause dîner";

function normalizeRouteSequenceManualBufferMinutes(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.round(numericValue) : null;
}

function buildRouteSequenceManualBufferChip(item, keyPrefix = "segment") {
  const normalizedBufferMinutes = normalizeRouteSequenceManualBufferMinutes(item?.manualBufferMinutes);
  if (normalizedBufferMinutes == null) {
    return null;
  }

  const bufferNote = typeof item?.manualBufferNote === "string" ? item.manualBufferNote.trim() : "";
  const reasonLabel = typeof item?.manualBufferReasonLabel === "string" && item.manualBufferReasonLabel.trim()
    ? item.manualBufferReasonLabel.trim()
    : formatManualBufferReasonLabel(item?.manualBufferReason) || "";
  const isSystemMixProductBuffer = bufferNote === MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE;
  const isLunchBreak = bufferNote === LUNCH_BREAK_BUFFER_NOTE;
  const hoverCardTitle = isSystemMixProductBuffer ? "Délai mix produit" : isLunchBreak ? LUNCH_BREAK_BUFFER_NOTE : "Buffer manuel";

  return {
    key: `${keyPrefix}:manual_buffer`,
    kind: "manual_buffer",
    label: isLunchBreak ? `Dîner ${normalizedBufferMinutes} min` : `+${normalizedBufferMinutes} min`,
    tone: "buffer",
    hoverCardDetails: {
      lines: [
        { label: "Durée", value: `+${normalizedBufferMinutes} min` },
        bufferNote ? { label: "Paramètre", value: bufferNote } : null,
        reasonLabel ? { label: "Catégorie", value: reasonLabel } : null,
      ].filter(Boolean),
      note: "Ce temps s'ajoute à la durée affichée du segment sur le Gantt, après le trajet routier et le temps de service. Il n'est pas inclus dans le kilométrage ni dans le temps de chargement ou de déchargement.",
      total: `+${normalizedBufferMinutes} min`,
      totalLabel: "Buffer segment",
    },
    hoverCardTitle,
    title: `${isSystemMixProductBuffer ? MANUAL_MIX_INTER_PRODUCT_BUFFER_NOTE : hoverCardTitle} : +${normalizedBufferMinutes} min`,
  };
}

function buildRouteSequenceGroupManualBufferChip(memberSegments, groupKey = "group") {
  const segmentsWithBuffer = (Array.isArray(memberSegments) ? memberSegments : [])
    .filter((segment) => normalizeRouteSequenceManualBufferMinutes(segment?.manualBufferMinutes) != null);
  if (!segmentsWithBuffer.length) {
    return null;
  }

  const totalBufferMinutes = segmentsWithBuffer.reduce(
    (total, segment) => total + (normalizeRouteSequenceManualBufferMinutes(segment?.manualBufferMinutes) || 0),
    0,
  );
  const primaryBufferSegment = segmentsWithBuffer.find((segment) => (
    typeof segment?.manualBufferNote === "string" && segment.manualBufferNote.trim()
  )) || segmentsWithBuffer[0];

  return buildRouteSequenceManualBufferChip({
    ...primaryBufferSegment,
    manualBufferMinutes: totalBufferMinutes,
  }, groupKey);
}

function resolveRouteSequenceDisplayedServiceDurationMinutes(rawServiceMinutes, meunerieExtraMinutes = 0, aspiratorServiceMinutes = 0) {
  const normalizedRaw = Number(rawServiceMinutes);
  if (!Number.isFinite(normalizedRaw) || normalizedRaw < 0) {
    return null;
  }

  const normalizedMeunerieExtra = Math.max(0, Math.round(Number(meunerieExtraMinutes) || 0));
  const normalizedAspiratorExtra = Math.max(0, Math.round(Number(aspiratorServiceMinutes) || 0));
  const normalizedExtra = normalizedMeunerieExtra + normalizedAspiratorExtra;
  if (normalizedExtra <= 0 || normalizedRaw < normalizedExtra) {
    return normalizedRaw;
  }

  return normalizedRaw - normalizedExtra;
}

function resolveRouteSequenceGroupDisplayedServiceDurationMinutes(memberSegments = [], totalServiceDurationMinutes = 0) {
  const totalExtraMinutes = resolveRouteSequenceGroupMeunerieExtraMinutes(memberSegments)
    + resolveRouteSequenceGroupAspiratorServiceMinutes(memberSegments);

  return resolveRouteSequenceDisplayedServiceDurationMinutes(totalServiceDurationMinutes, totalExtraMinutes);
}

function truncateRouteSequenceProductLabel(label = "", maxLength = ROUTE_SEQUENCE_PRODUCT_CHIP_MAX_LENGTH) {
  if (typeof label !== "string") {
    return "";
  }

  const trimmedLabel = label.trim();
  if (!trimmedLabel) {
    return "";
  }

  const characters = Array.from(trimmedLabel);
  if (characters.length <= maxLength) {
    return trimmedLabel;
  }

  const safeLength = Math.max(maxLength - 3, 1);
  return `${characters.slice(0, safeLength).join("")}...`;
}

function getRouteSequenceDisplayChips(chips = []) {
  const items = Array.isArray(chips) ? chips.filter(Boolean) : [];

  return items.map((chip) => {
    if (chip?.kind !== "grain" || typeof chip?.label !== "string") {
      if ((chip?.key === "svc" || chip?.key === "dur") && typeof chip?.label === "string") {
        const compactLabel = compactRouteSequenceDurationLabel(chip.label);
        return {
          ...chip,
          label: compactLabel,
          title: typeof chip?.title === "string" ? chip.title.replace(chip.label, compactLabel) : chip.title,
        };
      }

      return chip;
    }

    return {
      ...chip,
      label: truncateRouteSequenceProductLabel(chip.label),
    };
  });
}

function isInformationalRouteSequenceLoadItem(item = null) {
  const items = Array.isArray(item) ? item.filter(Boolean) : [item].filter(Boolean);
  return items.some((entry) => entry?.isInitialPreload || entry?.isEndOfDayLoadSourcePreload || entry?.isEndOfDayLoad);
}

function isRouteSequenceInformationalDurationChip(chip = null) {
  const chipKind = typeof chip?.kind === "string"
    ? chip.kind.trim()
    : typeof chip?.key === "string"
      ? chip.key.trim()
      : "";

  return chipKind === "dur" || chipKind === "svc";
}

function getRouteSequenceStepBadgeLabel(item = null) {
  // Chargé PM de plusieurs commandes : « 28-30 », comme dans le Gantt.
  if (item?.isPmOperationalLoading && item?.mergedOrderSequenceLabel) {
    return item.mergedOrderSequenceLabel;
  }

  const displaySequence = Number(item?.routeSequenceDisplaySequence);
  if (Number.isInteger(displaySequence) && displaySequence > 0) {
    return String(displaySequence);
  }

  const globalSequence = Number(item?.globalSequence);
  if (Number.isInteger(globalSequence) && globalSequence > 0) {
    return String(globalSequence);
  }

  const routeSegmentIndex = typeof item?.routeSegmentIndex === "string"
    ? item.routeSegmentIndex.trim()
    : `${item?.routeSegmentIndex ?? ""}`.trim();

  if (!routeSegmentIndex) {
    return "";
  }

  const trailingDigitsMatch = routeSegmentIndex.match(/(\d+)$/);
  return trailingDigitsMatch ? trailingDigitsMatch[1] : routeSegmentIndex;
}

function parseRouteSequenceTimestampClockMinutes(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return (parsedDate.getHours() * 60) + parsedDate.getMinutes() + (parsedDate.getSeconds() / 60) + (parsedDate.getMilliseconds() / 60000);
}

function formatRouteSequenceBoundaryLabel(item, timestampKey, minutesKey) {
  const timestampMinutes = parseRouteSequenceTimestampClockMinutes(item?.[timestampKey]);
  if (timestampMinutes != null) {
    return formatTimelineClockLabel(timestampMinutes);
  }

  const fallbackMinutes = Number(item?.[minutesKey]);
  return Number.isFinite(fallbackMinutes) ? formatTimelineClockLabel(fallbackMinutes) : "";
}

function getRouteSequenceExactStartLabel(item = null) {
  if (item?.isTerminalRouteMarker) {
    return formatRouteSequenceBoundaryLabel(item, "displayedSegmentEndAt", "displayedSegmentEndMinutes")
      || formatRouteSequenceBoundaryLabel(item, "visualSegmentStartAt", "visualSegmentStartMinutes")
      || formatRouteSequenceBoundaryLabel(item, "segmentStartAt", "segmentStartMinutes")
      || formatRouteSequenceBoundaryLabel(item, "scheduledAt", "scheduledAtMinutes");
  }

  if (item?.isEndOfDayLoadSourcePreload || item?.isEndOfDayLoad) {
    return END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_DIAGONAL_LABEL;
  }

  if (isPreviousDayPreloadDisplaySegment(item)) {
    return INITIAL_PRELOAD_ROUTE_SEQUENCE_DIAGONAL_LABEL;
  }

  return formatRouteSequenceBoundaryLabel(item, "visualSegmentStartAt", "visualSegmentStartMinutes")
    || formatRouteSequenceBoundaryLabel(item, "segmentStartAt", "segmentStartMinutes")
    || formatRouteSequenceBoundaryLabel(item, "scheduledAt", "scheduledAtMinutes");
}

function RouteSequenceStepLead({ children = null, hideStartLabel = false, indicators = [], startLabel = "" }) {
  const resolvedIndicators = hideStartLabel
    ? []
    : (Array.isArray(indicators) ? indicators.filter(Boolean) : []);
  const resolvedStartLabel = typeof startLabel === "string" ? startLabel.trim() : "";
  const shouldShowStartLabel = !hideStartLabel;

  return (
    <div className="grid w-[5.9rem] flex-none gap-0.5">
      <div className="grid grid-cols-[3.2rem_minmax(0,1fr)] items-center gap-1 tabular-nums">
        {shouldShowStartLabel ? (
          <span className="text-left text-[0.72rem] font-bold text-text">{resolvedStartLabel || "—"}</span>
        ) : (
          <span aria-hidden="true" />
        )}
        <div className="min-w-0">{children}</div>
      </div>
      {resolvedIndicators.length ? (
        <RouteSequenceIndicatorPills
          indicators={resolvedIndicators}
          className="w-full justify-start gap-0.5"
          pillClassName="px-1.5 py-px text-[0.58rem] tracking-[0.04em]"
        />
      ) : null}
    </div>
  );
}

function InitialPreloadCornerRibbon({
  label = INITIAL_PRELOAD_ROUTE_SEQUENCE_DIAGONAL_LABEL,
  title = INITIAL_PRELOAD_ROUTE_SEQUENCE_TITLE,
}) {
  return (
    <span
      aria-hidden="true"
      title={title}
      className={joinClasses([
        "pointer-events-none absolute z-10 inline-flex w-14 items-center justify-center border border-[rgba(88,106,145,0.26)] bg-[rgba(244,247,252,0.96)] px-1 py-px text-[0.43rem] font-black uppercase tracking-[0.12em] text-[rgb(84,100,137)] shadow-[0_4px_10px_rgba(15,23,42,0.08)]",
        DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
      ])}
      style={{ left: "-0.75rem", top: "0.1rem", transform: "rotate(-34deg)" }}
    >
      {label}
    </span>
  );
}

function EndOfDayLoadSourceCornerRibbon({
  label = END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_DIAGONAL_LABEL,
  title = END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_TITLE,
}) {
  return (
    <span
      aria-hidden="true"
      title={title}
      className={joinClasses([
        "pointer-events-none absolute z-20 inline-flex h-3.5 min-w-7 items-center justify-center border border-white/80 bg-[rgb(217,70,100)] px-1.5 py-0 text-[0.58rem] font-black uppercase leading-none tracking-[0.03em] text-white shadow-[0_4px_10px_rgba(136,19,55,0.22)]",
        DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
      ])}
      style={{ left: "0.05rem", top: "0.05rem", transform: "none" }}
    >
      {label}
    </span>
  );
}

function getSequenceGroupCompartmentLabel(memberSegments = []) {
  return joinGroupedSegmentCompartmentLabel(memberSegments);
}

function parseRouteSequenceCompartmentTokens(label = "") {
  if (typeof label !== "string") {
    return [];
  }

  return Array.from(new Set(
    label
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean),
  ));
}

function getRouteSequenceSegmentCompartmentTokens(item = null) {
  const chips = Array.isArray(item?.chips) ? item.chips : [];
  const compartmentChip = chips.find((chip) => chip?.key === "compartment" || chip?.kind === "compartment");

  if (!compartmentChip || typeof compartmentChip?.label !== "string") {
    return [];
  }

  return parseRouteSequenceCompartmentTokens(compartmentChip.label);
}

function applyRouteSequenceCompartmentOccupancyDelta(occupiedCompartments, item = null) {
  if (!item) {
    return occupiedCompartments;
  }

  const chips = Array.isArray(item?.chips) ? item.chips : [];
  const compartmentChip = chips.find((chip) => chip?.key === "compartment" || chip?.kind === "compartment");
  return applyCompartmentOccupancyChange(occupiedCompartments, {
    tokens: getRouteSequenceSegmentCompartmentTokens(item),
    stopType: typeof item?.stopType === "string" ? item.stopType.trim() : "",
    occupantOrderIdsByToken: compartmentChip?.occupantOrderIdsByToken,
  });
}

function formatRouteSequenceCompartmentOccupancyLabel(occupiedCount = 0, totalCompartments = 0) {
  const safeTotalCompartments = Math.max(Number(totalCompartments) || 0, 0);

  if (safeTotalCompartments <= 0) {
    return "";
  }

  const safeOccupiedCount = Math.max(
    Math.min(Number(occupiedCount) || 0, safeTotalCompartments),
    0,
  );

  return `${safeOccupiedCount}/${safeTotalCompartments}`;
}

function buildRouteSequenceCompartmentOccupancyMap(renderItems = [], totalCompartments = 0) {
  const occupancyByItemKey = new Map();
  const occupiedCompartments = new Map();
  const applyItems = (targetOccupancy, items = []) => {
    items.forEach((item) => {
      applyRouteSequenceCompartmentOccupancyDelta(targetOccupancy, item);
    });
  };

  renderItems.forEach((item) => {
    if (item?.kind === "group") {
      const previewOccupancy = cloneCompartmentOccupancy(occupiedCompartments);
      applyItems(previewOccupancy, Array.isArray(item.memberSegments) ? item.memberSegments : []);
      occupancyByItemKey.set(
        item.key,
        formatRouteSequenceCompartmentOccupancyLabel(previewOccupancy.size, totalCompartments),
      );

      if (!item.isExpanded) {
        occupiedCompartments.clear();
        previewOccupancy.forEach((orderIds, token) => {
          occupiedCompartments.set(token, orderIds);
        });
      }

      return;
    }

    applyRouteSequenceCompartmentOccupancyDelta(occupiedCompartments, item?.segment || item);
    occupancyByItemKey.set(
      item?.key,
      formatRouteSequenceCompartmentOccupancyLabel(occupiedCompartments.size, totalCompartments),
    );
  });

  return occupancyByItemKey;
}

function shouldDisplayRouteSequenceCompartmentOccupancy(item = null) {
  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";

  return stopType !== "depot_start"
    && stopType !== "depot_return"
    && item?.isTerminalRouteMarker !== true;
}

function buildRouteSequenceOccupancyChip(label = "", key = "", stopType = "") {
  if (!label) {
    return null;
  }

  return {
    key: `${key || "route"}:occupancy`,
    kind: "occupancy",
    label,
    stopType,
    title: `Occupation des compartiments : ${label}`,
  };
}

function insertRouteSequenceOccupancyChip(chips = [], occupancyChip = null) {
  const items = Array.isArray(chips) ? chips.filter(Boolean) : [];

  if (!occupancyChip) {
    return items;
  }

  const nextItems = [];
  let inserted = false;

  items.forEach((chip) => {
    nextItems.push(chip);

    if (!inserted && chip?.kind === "compartment") {
      nextItems.push(occupancyChip);
      inserted = true;
    }
  });

  if (!inserted) {
    nextItems.unshift(occupancyChip);
  }

  return nextItems;
}

function getSequenceGroupProductLabel(memberSegments = []) {
  const labels = Array.from(new Set(
    memberSegments
      .map((segment) => (typeof segment?.productLabel === "string" ? segment.productLabel.trim() : ""))
      .filter(Boolean),
  ));

  if (!labels.length) {
    return "";
  }

  return labels.length === 1 ? labels[0] : "Multiples";
}

function formatSequenceGroupTonnageLabel(memberSegments = []) {
  const totalTons = memberSegments.reduce((total, segment) => {
    const quantityTons = Number(segment?.quantityTons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);

  if (totalTons <= 0) {
    return "";
  }

  return `${formatTons(totalTons)} t`;
}

function compactRouteSequenceDurationLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return label.replace(/(\d+\s*h\s+\d+)\s*min\b/giu, "$1").trim();
}

function buildRouteSequenceDisplayChips(item, displayOverrides, fallbackIndex = 0, sequenceItems = [], hovercardContext = null) {
  const displayDistanceLabel = getSameSiteSequenceDisplayDistanceLabel(item, displayOverrides, fallbackIndex);
  const displayedTravelDuration = resolveSameSiteSequenceDisplayedTravelDuration(
    item,
    displayOverrides,
    fallbackIndex,
    hovercardContext,
  );
  const displayTravelDurationMinutes = displayedTravelDuration.minutes;
  const showsHereTrafficDuration = displayedTravelDuration.usesHereTraffic === true;
  const rawDistanceLabel = typeof item?.distanceLabel === "string" ? item.distanceLabel.trim() : "";
  const shouldOverrideDistanceChip = Boolean(displayDistanceLabel && displayDistanceLabel !== rawDistanceLabel);
  const shouldOverrideTravelChip = shouldSyncDisplayedTravelDurationChip(
    item,
    displayTravelDurationMinutes,
    { showsHereTrafficDuration },
  );

  const resolvedChips = getRouteSequenceDisplayChips(item?.chips);
  const meunerieChip = buildRouteSequenceMeunerieChip(
    resolveRouteSequenceMeunerieExtraMinutes(item),
    item?.key || item?.routeSegmentIndex || fallbackIndex + 1,
  );
  const aspiratorTimeChip = buildRouteSequenceAspiratorTimeChip(
    resolveRouteSequenceAspiratorServiceMinutes(item),
    item?.key || item?.routeSegmentIndex || fallbackIndex + 1,
  );
  const manualBufferChip = buildRouteSequenceManualBufferChip(
    item,
    item?.key || item?.routeSegmentIndex || fallbackIndex + 1,
  );
  const serviceExtraChips = [
    meunerieChip && !resolvedChips.some((chip) => chip?.kind === "meunerie" || chip?.key === "meunerie") ? meunerieChip : null,
    aspiratorTimeChip && !resolvedChips.some((chip) => chip?.kind === "aspirator_time" || chip?.key === "aspirator_time") ? aspiratorTimeChip : null,
    manualBufferChip && !resolvedChips.some((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer") ? manualBufferChip : null,
  ].filter(Boolean);
  const chipsWithServiceExtras = serviceExtraChips.length
    ? [
        ...resolvedChips.filter((chip) => chip?.kind !== "dur" && chip?.key !== "dur" && chip?.kind !== "manual_buffer" && chip?.key !== "manual_buffer"),
        ...serviceExtraChips.filter((chip) => chip?.kind !== "manual_buffer" && chip?.key !== "manual_buffer"),
        ...resolvedChips.filter((chip) => chip?.kind === "dur" || chip?.key === "dur"),
        ...serviceExtraChips.filter((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer"),
      ]
    : resolvedChips;
  const shouldHideInformationalDurationChips = isInformationalRouteSequenceLoadItem(item);

  return chipsWithServiceExtras
    .filter((chip) => !(shouldHideInformationalDurationChips && isRouteSequenceInformationalDurationChip(chip)))
    .map((chip) => {
      if (chip?.kind === "qty" && ["loading", "delivery"].includes(item?.stopType)) {
        return {
          ...chip,
          displayLabel: `${item.stopType === "loading" ? "+" : "−"}${chip.label}`,
          variant: `quantity-${item.stopType}`,
        };
      }
      if (chip?.key === "dist" && shouldOverrideDistanceChip) {
        return {
          ...chip,
          hoverCardDetails: buildRouteSequenceDistanceHoverCard(
            item,
            displayDistanceLabel,
            rawDistanceLabel,
            sequenceItems,
            hovercardContext,
          ),
          hoverCardTitle: "Distance affichée",
          label: displayDistanceLabel,
          title: `Distance affichée : ${displayDistanceLabel}`,
        };
      }

      if (chip?.key === "dur" && shouldOverrideTravelChip) {
        const displayLabel = formatRouteSequenceTravelDurationLabel(displayTravelDurationMinutes);
        return {
          ...chip,
          hoverCardDetails: buildRouteSequenceDurationHoverCard(
            item,
            displayDistanceLabel,
            displayTravelDurationMinutes,
            sequenceItems,
            { showsHereTrafficDuration },
          ),
          hoverCardTitle: resolveRouteSequenceDisplayedTravelLabel(showsHereTrafficDuration),
          label: displayLabel,
          title: buildRouteSequenceDisplayedTravelTitle(displayLabel, showsHereTrafficDuration),
        };
      }

      if (chip?.key === "svc" || chip?.kind === "svc") {
        const displayedServiceMinutes = resolveRouteSequenceDisplayedServiceDurationMinutes(
          item?.serviceDurationMinutes,
          resolveRouteSequenceMeunerieExtraMinutes(item),
          resolveRouteSequenceAspiratorServiceMinutes(item),
        );
        if (displayedServiceMinutes != null) {
          const displayLabel = compactRouteSequenceDurationLabel(formatRouteSequenceDurationChipLabel(displayedServiceMinutes));
          return {
            ...chip,
            label: displayLabel,
            title: `Temps de service : ${displayLabel}`,
          };
        }
      }

      return chip;
    });
}

function getRouteSequenceOperationLabel(item) {
  return getRouteSequencePrimaryTitle(item);
}

function getRouteSequenceOperationAddress(item) {
  return getRouteSequencePrimaryAddress(item);
}

function getRouteSequenceOperationTypeLabel(item) {
  if (item?.stopType === "depot_return" || isInitialRouteSequenceTravelSegment(item)) {
    return getRouteSequencePrimaryTitle(item);
  }

  return item?.operationTypeLabel || item?.to?.typeLabel || item?.to?.iconTitle || "Étape";
}

function getRouteSequenceOperationStopType(item) {
  return item?.stopType || item?.to?.stopType || "delivery";
}

function getGroupedRouteSequenceRouteSummaryLabel(groupItem, sequenceItems = []) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];

  for (let index = memberSegments.length - 1; index >= 0; index -= 1) {
    const routeSummaryLabel = getRouteSequenceDisplaySummaryLabel(sequenceItems, memberSegments[index], sequenceItems.indexOf(memberSegments[index]));
    if (routeSummaryLabel) {
      return routeSummaryLabel;
    }
  }

  return "";
}

function buildGroupedRouteSequenceAggregateChips(groupItem, displayOverrides, occupancyLabel = "", hovercardContext = null) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  if (!memberSegments.length) {
    return [];
  }

  const compartmentLabel = getSequenceGroupCompartmentLabel(memberSegments);
  const productLabel = getSequenceGroupProductLabel(memberSegments);
  const quantityLabel = formatSequenceGroupTonnageLabel(memberSegments);
  const distanceLabel = formatSequenceGroupDistanceLabel(memberSegments, displayOverrides);
  const displayedTravelDurations = memberSegments.map((segment, memberIndex) => {
    return resolveSameSiteSequenceDisplayedTravelDuration(segment, displayOverrides, memberIndex, hovercardContext);
  });
  const totalTravelDurationMinutes = displayedTravelDurations.reduce(
    (total, durationEntry) => total + durationEntry.minutes,
    0,
  );
  const showsHereTrafficDuration = displayedTravelDurations.some((durationEntry) => durationEntry.usesHereTraffic === true);
  const totalServiceDurationMinutes = memberSegments.reduce((total, segment) => total + (Number(segment?.serviceDurationMinutes) >= 0 ? Number(segment.serviceDurationMinutes) : 0), 0);
  const displayedServiceDurationMinutes = resolveRouteSequenceGroupDisplayedServiceDurationMinutes(memberSegments, totalServiceDurationMinutes)
    ?? totalServiceDurationMinutes;
  const shouldHideInformationalDurationChips = isInformationalRouteSequenceLoadItem(groupItem) || isInformationalRouteSequenceLoadItem(memberSegments);
  const hasServiceChip = !shouldHideInformationalDurationChips && memberSegments.some((segment) => segment?.serviceDurationLabel != null);
  const distinctProductLabels = Array.from(new Set(
    memberSegments
      .map((segment) => (typeof segment?.productLabel === "string" ? segment.productLabel.trim() : ""))
      .filter(Boolean),
  ));

  const chips = [
    compartmentLabel
      ? {
          key: `${groupItem?.groupKey || "group"}:compartment`,
          kind: "compartment",
          label: compartmentLabel,
          stopType: groupItem?.stopType || "delivery",
          title: `Compartiment(s) : ${compartmentLabel}`,
        }
      : null,
    productLabel
      ? {
          key: `${groupItem?.groupKey || "group"}:product`,
          kind: "grain",
          label: truncateRouteSequenceProductLabel(productLabel),
          title: distinctProductLabels.length > 1 ? `Produits : ${distinctProductLabels.join(", ")}` : productLabel,
        }
      : null,
    quantityLabel
      ? {
          key: `${groupItem?.groupKey || "group"}:qty`,
          kind: "qty",
          label: quantityLabel,
          displayLabel: ["loading", "delivery"].includes(groupItem?.stopType)
            ? `${groupItem.stopType === "loading" ? "+" : "−"}${quantityLabel}`
            : quantityLabel,
          variant: `quantity-${groupItem?.stopType}`,
          title: `Total tonnage : ${quantityLabel}`,
        }
      : null,
    distanceLabel
      ? {
          key: `${groupItem?.groupKey || "group"}:dist`,
          kind: "dist",
          hoverCardDetails: buildGroupedRouteSequenceDistanceHoverCard(groupItem, memberSegments, distanceLabel, hovercardContext),
          hoverCardTitle: "Distance regroupée",
          label: distanceLabel,
          title: `Distance totale : ${distanceLabel}`,
        }
      : null,
    hasServiceChip
      ? {
          key: `${groupItem?.groupKey || "group"}:svc`,
          kind: "svc",
          label: formatRouteSequenceDurationChipLabel(displayedServiceDurationMinutes),
          title: `Temps de service total : ${formatRouteSequenceDurationChipLabel(displayedServiceDurationMinutes)}`,
        }
      : null,
    groupItem?.stopType === "loading"
      ? buildRouteSequenceMeunerieChip(
        resolveRouteSequenceGroupMeunerieExtraMinutes(memberSegments),
        groupItem?.groupKey || groupItem?.key || "group",
      )
      : null,
    resolveRouteSequenceGroupAspiratorServiceMinutes(memberSegments) > 0
      ? buildRouteSequenceAspiratorTimeChip(
        resolveRouteSequenceGroupAspiratorServiceMinutes(memberSegments),
        groupItem?.groupKey || groupItem?.key || "group",
      )
      : null,
    buildRouteSequenceGroupManualBufferChip(memberSegments, groupItem?.groupKey || groupItem?.key || "group"),
    shouldHideInformationalDurationChips
      ? null
      : {
          key: `${groupItem?.groupKey || "group"}:dur`,
          kind: "dur",
          hoverCardDetails: buildGroupedRouteSequenceDurationHoverCard(
            groupItem,
            memberSegments,
            distanceLabel,
            totalTravelDurationMinutes,
            { showsHereTrafficDuration },
          ),
          hoverCardTitle: resolveRouteSequenceDisplayedTravelLabel(showsHereTrafficDuration),
          label: formatRouteSequenceDurationChipLabel(totalTravelDurationMinutes, { approximate: true }),
          title: buildRouteSequenceDisplayedTravelTitle(
            formatRouteSequenceDurationChipLabel(totalTravelDurationMinutes, { approximate: true }),
            showsHereTrafficDuration,
          ),
        },
  ].filter(Boolean);

  return insertRouteSequenceOccupancyChip(
    chips,
    shouldDisplayRouteSequenceCompartmentOccupancy(groupItem)
      ? buildRouteSequenceOccupancyChip(occupancyLabel, groupItem?.groupKey || groupItem?.key || "group", groupItem?.stopType || "")
      : null,
  );
}

function getGroupedRouteMarkerStyle(groupItem, fallbackColor = "") {
  if (groupItem?.stopType === "mixed") {
    return {
      background: "linear-gradient(135deg, var(--dispatch-sequence-loading-marker-fill, var(--route-loading)) 0%, var(--dispatch-sequence-delivery-marker-fill, var(--route-delivery)) 100%)",
      boxShadow: "0 0 0 1px rgba(100,116,139,0.28), 0 0 0 4px rgba(148,163,184,0.16), 0 3px 8px rgba(15,23,42,0.12)",
    };
  }

  if (groupItem?.stopType === "loading") {
    return { backgroundColor: fallbackColor || "var(--dispatch-sequence-loading-marker-fill, var(--route-loading))" };
  }

  if (groupItem?.stopType === "delivery") {
    return { backgroundColor: fallbackColor || "var(--dispatch-sequence-delivery-marker-fill, var(--route-delivery))" };
  }

  return { backgroundColor: fallbackColor || "var(--dispatch-sequence-return-marker-fill, var(--color-legacy-teal))" };
}

function getGroupedRouteAccentColor(groupItem, fallbackColor = "") {
  if (groupItem?.stopType === "delivery") {
    return fallbackColor || "var(--route-delivery)";
  }

  if (groupItem?.stopType === "loading") {
    return fallbackColor || "var(--route-loading)";
  }

  if (groupItem?.stopType === "mixed") {
    return fallbackColor || "rgba(148,163,184,0.82)";
  }

  return fallbackColor || "var(--color-legacy-teal)";
}

function SequenceGroupPill({ label = "" }) {
  if (!label) {
    return null;
  }

  return (
    <span
      className={joinClasses([
        "inline-flex min-w-[7.1rem] shrink-0 items-center justify-center whitespace-nowrap border border-[rgba(148,163,184,0.2)] bg-[rgba(248,250,252,0.86)] px-3 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.06em] text-content-muted",
        DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
      ])}
      data-route-pill="true"
    >
      {label}
    </span>
  );
}

function GroupedRouteJourneyConnector({ label = "" }) {
  if (!label) {
    return <div className="hidden lg:block" aria-hidden="true" />;
  }

  return (
    <div className="hidden w-full items-center justify-center lg:flex" aria-hidden="true">
      <SequenceGroupPill label={label} />
    </div>
  );
}

function RouteAddressHoverCardContent({ address = "", eyebrow = "Destination", label = "" }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-content-muted">{eyebrow}</div>
      {label ? <div className="text-[12px] font-semibold leading-[1.45] text-content-ink">{label}</div> : null}
      <div className="text-[11px] leading-normal text-content-muted">{address}</div>
    </div>
  );
}

function RouteStopAddressHoverCard({
  accentColor = "var(--route-loading)",
  address = "",
  children,
  eyebrow = "Destination",
  label = "",
  placement = "top",
  title = "",
}) {
  const trimmedAddress = typeof address === "string" ? address.trim() : "";

  if (!trimmedAddress) {
    return children;
  }

  return (
    <HoverCard
      title={title}
      content={<RouteAddressHoverCardContent address={trimmedAddress} eyebrow={eyebrow} label={label} />}
      placement={placement}
      maxWidth={320}
      accentColor={accentColor}
    >
      {children}
    </HoverCard>
  );
}

function getRouteStopAccentColor(stopType, fallbackColor = "var(--route-loading)") {
  if (stopType === "delivery") {
    return "var(--route-delivery)";
  }

  if (stopType === "loading") {
    return "var(--route-loading)";
  }

  if (stopType === "depot_return" || stopType === "depot_start") {
    return "var(--color-legacy-teal)";
  }

  return fallbackColor || "var(--route-loading)";
}

function renderTransferStopIcon(stopType) {
  const transferLabel = stopType === "loading" ? "C" : "D";

  return (
    <span className="flex h-full w-full items-center justify-center rounded-full">
      <span className="text-[1.30rem] font-black uppercase leading-none tracking-[-0.09em] [-webkit-text-stroke:0.35px_currentColor] transition-transform duration-300 group-hover/segment:-translate-y-0.5">
        {transferLabel}
      </span>
    </span>
  );
}

function renderRouteStopIcon(stopType) {
  if (stopType === "loading") {
    return renderTransferStopIcon(stopType);
  }

  if (stopType === "depot_return") {
    return <House className="h-4 w-4" strokeWidth={2.15} />;
  }

  if (stopType === "delivery") {
    return renderTransferStopIcon(stopType);
  }

  return <House className="h-4 w-4" strokeWidth={2.15} />;
}

function RouteJourneyConnector({ className = "", orderLabel = "", stopType = "" }) {
  const trimmedOrderLabel = typeof orderLabel === "string" ? orderLabel.trim() : "";
  const connectorLabel = trimmedOrderLabel || (stopType === "loading"
    ? "Chargement"
    : stopType === "delivery"
      ? "Déchargement"
      : "");

  return (
    <div className={joinClasses(["hidden min-w-0 w-full items-center justify-center lg:flex", className])} aria-hidden="true">
      {connectorLabel ? (
        <span
          className={joinClasses([
            "inline-flex h-6 max-w-full min-w-0 items-center justify-center border bg-white px-1.5",
            DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
          ])}
          data-route-pill="true"
          style={{ borderColor: "var(--route-segment-end-color)", color: "#1f2937" }}
          title={trimmedOrderLabel || undefined}
        >
          <span className="min-w-0 truncate whitespace-nowrap text-[0.62rem] font-bold tabular-nums">{connectorLabel}</span>
        </span>
      ) : null}
    </div>
  );
}

// Compartiments en petits carrés, comme dans la Vue flotte.
function RouteSequenceCompartmentSquares({ chip, totalCompartments = 0 }) {
  const usedPositions = new Set(
    String(getRouteMetaChipDisplayLabel(chip) || "")
      .split(/[,\s]+/)
      .map((label) => Number(/^C0*(\d+)$/i.exec(label)?.[1]))
      .filter(Boolean),
  );
  const compartmentCount = Math.max(Number(totalCompartments) || 0, ...usedPositions);
  if (!compartmentCount) {
    return null;
  }

  const activeColor = chip?.stopType === "loading" ? "#5985b7" : chip?.stopType === "delivery" ? "#b68a34" : "#94a3b8";
  return (
    <span className="flex h-5 w-full items-center gap-[2px]" data-route-compartment-squares="true">
      {Array.from({ length: compartmentCount }, (_, index) => (
        <span
          key={index}
          aria-hidden="true"
          className="h-[13px] min-w-[3px] max-w-[8px] flex-1"
          style={{ backgroundColor: usedPositions.has(index + 1) ? activeColor : "#ebe8e2" }}
        />
      ))}
    </span>
  );
}

function buildRouteSequenceChipRenderer(totalCompartments = 0) {
  return (chip) => (chip?.kind === "compartment"
    ? <RouteSequenceCompartmentSquares chip={chip} totalCompartments={totalCompartments} />
    : null);
}

function RouteSequenceColumnHeader({ presentChipKinds = new Set() }) {
  const labelClassName = "min-w-0 truncate text-[0.56rem] font-bold uppercase tracking-[0.06em] text-content-muted";
  return (
    <div aria-hidden="true" className="hidden rounded-[4px] border border-transparent bg-[#eee9e3] px-2 py-0.75 lg:block" data-route-sequence-column-header="true">
      <div className="grid lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center lg:gap-2">
        <span className={joinClasses(["w-[5.9rem]", labelClassName])}>Heure · #</span>
        <div className={joinClasses(["grid min-w-0 lg:items-center lg:gap-2.5", ROUTE_SEQUENCE_DETAILS_GRID_CLASS_NAME])}>
          <span className={joinClasses([labelClassName, "pl-9"])}>Arrêt</span>
          <span className={joinClasses([labelClassName, "text-center"])}>Commande</span>
          <div className={ROUTE_SEQUENCE_CHIP_GRID_CLASS_NAME}>
            {ROUTE_SEQUENCE_CHIP_COLUMNS.map((column) => (!column.optional || presentChipKinds.has(column.kind) ? (
              <span key={column.kind} className={joinClasses([labelClassName, "text-center", ROUTE_SEQUENCE_CHIP_COLUMN_CLASS_NAME_MAP[column.kind]])}>
                {column.label}
              </span>
            ) : null))}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatInlineRouteSummaryLabel(label = "") {
  if (typeof label !== "string") {
    return "";
  }

  return label.replace(/^Trajet[^:]*:\s*/i, "").trim();
}

function formatRouteSequenceOrderTailLabel(orderLabel = "") {
  if (typeof orderLabel !== "string") {
    return "";
  }

  const trimmedOrderLabel = orderLabel.trim();
  if (!trimmedOrderLabel) {
    return "";
  }

  const trailingDigitsMatch = trimmedOrderLabel.match(/(\d{5})\D*$/);
  if (trailingDigitsMatch) {
    return trailingDigitsMatch[1];
  }

  const digitsOnlyLabel = trimmedOrderLabel.replace(/\D+/g, "");
  if (digitsOnlyLabel.length >= 5) {
    return digitsOnlyLabel.slice(-5);
  }

  return trimmedOrderLabel.length > 5 ? trimmedOrderLabel.slice(-5) : trimmedOrderLabel;
}

function getGroupedRouteConnectorOrderLabel(memberSegments = []) {
  const orderLabels = Array.from(new Set(
    memberSegments
      .map((segment) => formatRouteSequenceOrderTailLabel(segment?.operationOrderId || segment?.orderLabel || ""))
      .filter(Boolean),
  ));

  return orderLabels.join(" · ");
}

function buildRouteSequenceInlineIndicators(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean);
  const indicators = [];
  const aspiratorItem = resolvedItems.find((item) => item?.requiresAspirator) || null;
  const hasEndOfDayLoadSourcePreload = resolvedItems.some((item) => item?.isEndOfDayLoadSourcePreload || item?.isEndOfDayLoad);
  const hasInitialPreload = !hasEndOfDayLoadSourcePreload && resolvedItems.some((item) => item?.isInitialPreload);
  const manualMixBoundaryItem = resolvedItems.find((item) => item?.hasManualMixBoundaryBefore) || null;
  const priorityLabels = Array.from(new Set(
    resolvedItems
      .map((item) => (typeof item?.dominantPriority === "string" ? item.dominantPriority.trim().toUpperCase() : ""))
      .filter(Boolean),
  )).sort((leftLabel, rightLabel) => (ROUTE_SEQUENCE_PRIORITY_SORT_WEIGHT[rightLabel] || 0) - (ROUTE_SEQUENCE_PRIORITY_SORT_WEIGHT[leftLabel] || 0));
  const timeAnchorItem = resolvedItems.find((item) => item?.hasPriorityTimeAnchor && Number.isFinite(Number(item?.scheduledAtMinutes))) || null;

  if (aspiratorItem) {
    indicators.push({
      key: "aspirator",
      label: "Aspiration",
      tone: "aspiration",
      title: aspiratorItem?.aspiratorRequirementLabel || "Aspiration requise",
    });
  }

  if (manualMixBoundaryItem) {
    const currentLabel = typeof manualMixBoundaryItem?.manualMixCurrentProductLabel === "string"
      ? manualMixBoundaryItem.manualMixCurrentProductLabel.trim()
      : "";
    const previousLabel = typeof manualMixBoundaryItem?.manualMixPreviousProductLabel === "string"
      ? manualMixBoundaryItem.manualMixPreviousProductLabel.trim()
      : "";
    const label = currentLabel || previousLabel || "Mix produit";
    const accentColor = currentLabel || previousLabel ? getFleetProductTone(label).accentColor : "";

    indicators.push({
      key: `product-change:${label}`,
      label,
      // Même couleur pleine que les onglets produit du Gantt.
      style: accentColor ? { backgroundColor: accentColor, borderColor: accentColor, color: "#fff" } : undefined,
      tone: "product-change",
      title: manualMixBoundaryItem?.manualMixBoundaryTitle || "Changement de produit",
    });
  }

  if (hasInitialPreload) {
    indicators.push({
      key: "initial-preload",
      label: INITIAL_PRELOAD_ROUTE_SEQUENCE_PILL_LABEL,
      tone: "neutral",
      title: INITIAL_PRELOAD_ROUTE_SEQUENCE_TITLE,
    });
  }

  if (hasEndOfDayLoadSourcePreload) {
    indicators.push({
      key: "end-of-day-load-source",
      label: END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_PILL_LABEL,
      tone: "active",
      title: END_OF_DAY_LOAD_SOURCE_ROUTE_SEQUENCE_TITLE,
    });
  }

  priorityLabels.forEach((priorityLabel) => {
    indicators.push({ key: `priority:${priorityLabel}`, label: priorityLabel, tone: "active", title: `Priorité ${priorityLabel}` });
  });

  const openingItem = resolvedItems.find((item) => typeof item?.client_loading_opening_time === "string" && item.client_loading_opening_time);
  const openingIndicator = openingItem ? buildRouteSequenceOpeningIndicator(openingItem.client_loading_opening_time) : null;
  if (openingIndicator) {
    indicators.push(openingIndicator);
  }

  if (timeAnchorItem) {
    const formattedTime = formatTimelineClockLabel(timeAnchorItem.scheduledAtMinutes);
    if (formattedTime) {
      indicators.push({ key: `time:${formattedTime}`, label: formattedTime, tone: "schedule", title: `Horaire cible ${formattedTime}` });
    }
  }

  return indicators;
}

function hasRouteSequencePriorityTimeAnchor(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean);

  return resolvedItems.some((item) => item?.hasPriorityTimeAnchor && Number.isFinite(Number(item?.scheduledAtMinutes)));
}

function isRouteSequencePriorityScheduleIndicator(indicator = null) {
  const indicatorKey = typeof indicator?.key === "string" ? indicator.key.trim() : "";
  return indicatorKey.startsWith("priority:") || indicatorKey.startsWith("time:");
}

function splitRouteSequenceInlineIndicators(indicators = [], { promotePrioritySchedule = false } = {}) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];

  if (!promotePrioritySchedule) {
    return {
      inlineIndicators: resolvedIndicators,
      promotedIndicators: [],
    };
  }

  const promotedIndicators = [];
  const inlineIndicators = [];

  resolvedIndicators.forEach((indicator) => {
    if (isRouteSequencePriorityScheduleIndicator(indicator)) {
      promotedIndicators.push(indicator);
      return;
    }

    inlineIndicators.push(indicator);
  });

  return {
    inlineIndicators,
    promotedIndicators,
  };
}

const ROUTE_SEQUENCE_ALIGNED_STOP_PILL_CLASS_NAME = "min-w-[7.1rem] shrink-0 justify-center";

function RouteSequenceIndicatorPills({ className = "", indicators = [], pillClassName = "" }) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];

  if (!resolvedIndicators.length) {
    return null;
  }

  return (
    <div className={joinClasses(["flex flex-wrap items-center gap-1", className])}>
      {resolvedIndicators.map((indicator) => {
        const toneClassName = ROUTE_SEQUENCE_INDICATOR_TONE_CLASS_NAME_MAP[indicator.tone] || ROUTE_SEQUENCE_INDICATOR_TONE_CLASS_NAME_MAP.outline;

        return (
          <span
            key={indicator.key || indicator.label}
            className={joinClasses([
              "inline-flex min-w-0 items-center border px-1.75 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.06em] shadow-none",
              DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
              indicator.tone === "opening" ? ROUTE_SEQUENCE_ALIGNED_STOP_PILL_CLASS_NAME : "",
              toneClassName,
              pillClassName,
            ])}
            data-route-pill="true"
            style={indicator.style}
            title={indicator.title || undefined}
          >
            {indicator.label}
          </span>
        );
      })}
    </div>
  );
}

function RouteSequenceSupplementaryMeta({ connectorPill = null, indicators = [], summaryLabel = "", addressLabel = "" }) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  const resolvedSummaryLabel = typeof summaryLabel === "string" ? summaryLabel.trim() : "";
  const resolvedAddressLabel = typeof addressLabel === "string" ? addressLabel.trim() : "";
  const resolvedSecondaryLabel = resolvedSummaryLabel || resolvedAddressLabel;
  const hasPillRow = connectorPill || resolvedIndicators.length > 0;

  if (!hasPillRow && !resolvedSecondaryLabel) {
    return null;
  }

  return (
    <div className="grid min-w-0 gap-0.5 text-[0.64rem] leading-4 text-content-muted">
      {hasPillRow ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1.25 overflow-hidden">
          {connectorPill}
          <RouteSequenceIndicatorPills indicators={resolvedIndicators} />
        </div>
      ) : null}
      {resolvedSecondaryLabel ? (
        <span className="min-w-0 truncate" title={resolvedSecondaryLabel}>
          {resolvedSecondaryLabel}
        </span>
      ) : null}
    </div>
  );
}

function shouldHideRouteSequenceSupplementaryText(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean);
  return resolvedItems.some((item) => item?.isInitialPreload);
}

function GroupedRouteSegmentRow({ displayOverrides = null, groupItem, hovercardContext = null, interactions = {}, isExpanded = false, isHighlighted = false, occupancyLabel = "", onToggleExpand = null, sequenceItems = [], showOpeningTime = true, totalCompartments = 0 }) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  const firstSegment = memberSegments[0] || null;
  const lastSegment = memberSegments[memberSegments.length - 1] || firstSegment;
  const destinationSegment = groupItem?.mergedNextSegment || firstSegment || groupItem;
  const groupSequenceLabel = getGroupedSegmentSequenceLabel(groupItem);
  const groupAggregateChips = buildGroupedRouteSequenceAggregateChips(groupItem, displayOverrides, occupancyLabel, hovercardContext);
  const groupAccentColor = getGroupedRouteAccentColor(
    groupItem,
    lastSegment?.segmentEndColor || firstSegment?.segmentEndColor || firstSegment?.segmentColor || "",
  );
  const destinationStopType = getRouteSequenceOperationStopType(destinationSegment || groupItem);
  const destinationLabel = groupItem?.operationLabel || getRouteSequenceOperationLabel(destinationSegment || groupItem);
  const destinationAddress = groupItem?.operationAddress || getRouteSequenceOperationAddress(destinationSegment || groupItem);
  const routeSummaryLabel = getGroupedRouteSequenceRouteSummaryLabel(groupItem, sequenceItems);
  const inlineRouteSummaryLabel = formatInlineRouteSummaryLabel(routeSummaryLabel);
  const shouldHideSupplementaryText = shouldHideRouteSequenceSupplementaryText(memberSegments);
  const connectorLabel = groupSequenceLabel ? `Même arrêt ${groupSequenceLabel}` : `Même arrêt ${memberSegments.length}`;
  const inlineIndicators = buildRouteSequenceInlineIndicators(memberSegments);
  const {
    inlineIndicators: groupContentIndicators,
    promotedIndicators: groupStepIndicators,
  } = splitRouteSequenceInlineIndicators(inlineIndicators, {
    promotePrioritySchedule: hasRouteSequencePriorityTimeAnchor(memberSegments),
  });
  const visibleGroupContentIndicators = filterRouteSequenceOpeningIndicators(groupContentIndicators, showOpeningTime);
  const groupedOrderLabel = getGroupedRouteConnectorOrderLabel(memberSegments);
  const hoverRouteSegmentIndex = resolveGroupedHoverRouteSegmentIndex(groupItem?.memberSegments);
  const groupRouteNumber = Number(firstSegment?.routeSequenceDisplaySequence || groupItem?.startSequence || firstSegment?.globalSequence);
  const groupStartLabel = getRouteSequenceExactStartLabel(firstSegment);
  const canZoomToGroupStart = Number.isInteger(groupRouteNumber) && groupRouteNumber > 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onToggleExpand?.(groupItem.groupKey);

          if (isExpanded) {
            interactions.onRouteSequenceSelect?.({
              restoreView: true,
              routeSegmentIndex: hoverRouteSegmentIndex,
            });
            return;
          }

          if (!canZoomToGroupStart) {
            return;
          }

          interactions.onRouteSequenceSelect?.({
            routeNumber: groupRouteNumber,
            routeSegmentIndex: hoverRouteSegmentIndex,
          });
        }}
        className={joinClasses([
          "group/segment relative grid w-full gap-1 overflow-hidden border border-[rgba(0,0,0,0.22)] bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(246,249,252,0.97))] px-2 py-1.25 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_6px_16px_rgba(10,32,37,0.04)] transition duration-150 before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[8px] before:bg-(--route-group-color) hover:-translate-y-0.5 hover:border-[rgba(13,148,136,0.28)] hover:shadow-[0_18px_32px_rgba(16,28,41,0.08)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          DISPATCH_FRAME_RADIUS_CLASS_NAME,
          isExpanded ? "border-[rgba(43,57,69,0.34)] bg-[linear-gradient(180deg,rgba(242,246,248,0.98),rgba(255,255,255,0.98))] shadow-[0_12px_28px_rgba(43,57,69,0.08)]" : "",
          isHighlighted ? "-translate-y-0.5 border-[rgba(15,92,87,0.28)] shadow-[0_18px_32px_rgba(15,92,87,0.12)]" : "",
        ])}
        style={{ "--route-group-color": groupAccentColor }}
        aria-expanded={isExpanded ? "true" : "false"}
        aria-label={`${memberSegments.length} étapes au même arrêt ${destinationLabel}. ${isExpanded ? "Replier" : "Déplier"} le groupe.`.trim()}
        data-route-group-start-sequence={canZoomToGroupStart ? String(groupRouteNumber) : undefined}
        onFocus={() => {
          if (hoverRouteSegmentIndex != null) {
            interactions.onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
          }
        }}
        onBlur={() => {
          interactions.onRouteHoverEnd?.();
        }}
        onMouseEnter={() => {
          if (hoverRouteSegmentIndex != null) {
            interactions.onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
          }
        }}
        onMouseLeave={() => {
          interactions.onRouteHoverEnd?.();
        }}
      >
        <div className="grid gap-1.25 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center lg:gap-2">
          <RouteSequenceStepLead indicators={groupStepIndicators} startLabel={groupStartLabel}>
            {groupItem?.isEndOfDayLoadSourcePreload || groupItem?.isEndOfDayLoad
              ? <EndOfDayLoadSourceCornerRibbon />
              : groupItem?.isInitialPreload
                ? <InitialPreloadCornerRibbon />
                : null}
            <span className="relative inline-flex h-6.5 w-6.5 overflow-visible" aria-hidden="true">
              <span
                className="inline-flex h-6.5 w-6.5 items-center justify-center rounded-full border border-white/50 text-white shadow-[0_8px_16px_rgba(10,32,37,0.16)]"
                style={getGroupedRouteMarkerStyle(groupItem, groupAccentColor)}
              >
                <ChevronDown className={joinClasses(["h-3.5 w-3.5 transition-transform duration-150", isExpanded ? "rotate-180" : ""])} strokeWidth={2.6} />
              </span>
            </span>
          </RouteSequenceStepLead>

          <div className={joinClasses(["grid min-w-0 gap-1 lg:items-center lg:gap-2.5", ROUTE_SEQUENCE_DETAILS_GRID_CLASS_NAME])}>
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="inline-flex" aria-hidden="true">
                <span
                  className={joinClasses([
                    "inline-flex h-7.5 w-7.5 flex-none items-center justify-center overflow-hidden p-0",
                    DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
                    stopIconClassNames[destinationStopType] || stopIconClassNames.delivery,
                  ])}
                >
                  {renderRouteStopIcon(destinationStopType)}
                </span>
              </span>

              <div className="grid min-w-0 gap-px">
                <strong className="truncate text-[0.76rem] leading-tight text-text">{destinationLabel}</strong>
                <RouteSequenceSupplementaryMeta
                  connectorPill={<SequenceGroupPill label={connectorLabel} />}
                  indicators={visibleGroupContentIndicators}
                  summaryLabel={!shouldHideSupplementaryText ? inlineRouteSummaryLabel : ""}
                  addressLabel={!shouldHideSupplementaryText && !inlineRouteSummaryLabel ? destinationAddress : ""}
                />
              </div>
            </div>

            {groupedOrderLabel ? (
              <RouteJourneyConnector
                className="lg:justify-center"
                orderLabel={groupedOrderLabel}
                stopType={destinationStopType}
              />
            ) : <div className="hidden lg:block" aria-hidden="true" />}

            {groupAggregateChips.length ? (
              <RouteMetaChipRow
                chips={groupAggregateChips}
                chipColumnClassNameMap={ROUTE_SEQUENCE_CHIP_COLUMN_CLASS_NAME_MAP}
                className="lg:flex-nowrap lg:justify-end"
                gridClassName={ROUTE_SEQUENCE_CHIP_GRID_CLASS_NAME}
                renderChipContent={buildRouteSequenceChipRenderer(totalCompartments)}
              />
            ) : <div className="hidden lg:block" aria-hidden="true" />}
          </div>
        </div>
      </button>
    </li>
  );
}

export function RouteSegmentRow({ className = "", destinationClassName = "", displayOverrides = null, hovercardContext = null, interactions = {}, isGroupMember = false, item, itemIndex = 0, occupancyLabel = "", sequenceItems = [], showOpeningTime = true, totalCompartments = 0 }) {
  const isSelectable = Boolean(item?.isClientSelectable);
  const isHighlighted = item?.isHighlighted === true;
  const stepSequenceLabel = getRouteSequenceStepBadgeLabel(item);
  const stepStartLabel = getRouteSequenceExactStartLabel(item);
  const destinationLabel = getRouteSequenceOperationLabel(item);
  const destinationAddress = getRouteSequenceOperationAddress(item);
  const destinationTypeLabel = getRouteSequenceOperationTypeLabel(item);
  const destinationStopType = getRouteSequenceOperationStopType(item);
  const destinationAccentColor = getRouteStopAccentColor(destinationStopType, item?.segmentColor || item?.segmentEndColor);
  const routeSummaryLabel = getRouteSequenceDisplaySummaryLabel(sequenceItems, item, itemIndex);
  const inlineRouteSummaryLabel = formatInlineRouteSummaryLabel(routeSummaryLabel);
  const shouldHideSupplementaryText = shouldHideRouteSequenceSupplementaryText(item);
  const inlineIndicators = buildRouteSequenceInlineIndicators(item).filter((indicator) => !(isGroupMember && indicator?.key === "initial-preload"));
  const {
    inlineIndicators: routeContentIndicators,
    promotedIndicators: routeStepIndicators,
  } = splitRouteSequenceInlineIndicators(inlineIndicators, {
    promotePrioritySchedule: hasRouteSequencePriorityTimeAnchor(item),
  });
  const visibleRouteContentIndicators = filterRouteSequenceOpeningIndicators(routeContentIndicators, showOpeningTime)
    .filter((indicator) => !(isGroupMember && indicator?.tone === "product-change"));
  const displayChips = insertRouteSequenceOccupancyChip(
    buildRouteSequenceDisplayChips(item, displayOverrides, itemIndex, sequenceItems, hovercardContext),
    shouldDisplayRouteSequenceCompartmentOccupancy(item)
      ? buildRouteSequenceOccupancyChip(occupancyLabel, item?.key || item?.routeSegmentIndex || itemIndex + 1, item?.stopType || "")
      : null,
  ).filter((chip) => {
    if (!isGroupMember) {
      return true;
    }

    const chipKind = typeof chip?.kind === "string"
      ? chip.kind.trim()
      : typeof chip?.key === "string"
        ? chip.key.trim()
        : "";

    return !ROUTE_SEQUENCE_SUBGROUP_HIDDEN_CHIP_KINDS.has(chipKind);
  });
  const hasSupplementaryContent = visibleRouteContentIndicators.length > 0
    || (!shouldHideSupplementaryText && Boolean(inlineRouteSummaryLabel || destinationAddress));
  const leftSequenceBadgeColor = item?.stopType === "depot_start"
    ? "var(--color-legacy-teal)"
    : "var(--route-segment-end-color)";
  const leftSequenceBadgeTextClassName = item?.isInitialPreload && !item?.isEndOfDayLoadSourcePreload ? "text-slate-950" : "text-white";
  const routeNumber = Number(item?.routeSequenceDisplaySequence || item?.globalSequence);
  const canZoomToRouteBadge = !isSelectable && Number.isInteger(routeNumber) && routeNumber > 0;
  const isInteractive = isSelectable || canZoomToRouteBadge;
  const hoverRouteSegmentIndexes = resolveAspiratorMissionRouteSegmentIndexes(item);
  const elementProps = isSelectable
    ? {
        "aria-pressed": item.isSelected ? "true" : "false",
        "data-client-id": item.clientId,
        "data-client-selectable": "true",
        role: "button",
        tabIndex: 0,
      }
    : isInteractive
      ? {
          role: "button",
          tabIndex: 0,
        }
      : {};

  return (
    <li
      className={joinClasses([
        "group/segment relative grid gap-1 overflow-hidden border border-[rgba(0,0,0,0.22)] bg-[linear-gradient(180deg,rgba(255,255,255,0.99),rgba(246,249,252,0.97))] px-2 py-1.25 shadow-[inset_0_1px_0_rgba(255,255,255,0.82),0_6px_16px_rgba(10,32,37,0.04)] transition duration-150 before:absolute before:inset-y-0 before:left-0 before:w-1 before:rounded-l-[8px] before:bg-(--route-segment-color)",
        DISPATCH_FRAME_RADIUS_CLASS_NAME,
        isInteractive ? "cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 hover:-translate-y-0.5 hover:border-[rgba(13,148,136,0.28)] hover:shadow-[0_18px_32px_rgba(16,28,41,0.08)]" : "",
        item?.isSelected ? "border-[rgba(43,57,69,0.34)] bg-[linear-gradient(180deg,rgba(242,246,248,0.98),rgba(255,255,255,0.98))] shadow-[0_12px_28px_rgba(43,57,69,0.08)]" : "",
        isHighlighted ? "-translate-y-0.5 border-[rgba(15,92,87,0.28)] shadow-[0_18px_32px_rgba(15,92,87,0.12)]" : "",
        className,
      ])}
      style={{
        "--route-segment-color": item.segmentColor,
        "--route-segment-end-color": item.segmentEndColor,
        "--route-segment-start-color": item.segmentStartColor,
      }}
      onClick={() => {
        if (isSelectable) {
          interactions.onRouteClientSelect?.({
            centerMap: true,
            clientId: item.clientId,
            routeSegmentIndex: item.routeSegmentIndex,
          });
          return;
        }

        if (!canZoomToRouteBadge) {
          return;
        }

        interactions.onRouteSequenceSelect?.({
          routeNumber,
          routeSegmentIndex: item.routeSegmentIndex,
        });
      }}
      onFocus={() => {
        interactions.onRouteSegmentHoverStart?.(item.routeSegmentIndex, hoverRouteSegmentIndexes);
      }}
      onBlur={() => {
        interactions.onRouteHoverEnd?.();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        if (!isInteractive) {
          return;
        }

        event.preventDefault();
        if (isSelectable) {
          interactions.onRouteClientSelect?.({
            centerMap: true,
            clientId: item.clientId,
            routeSegmentIndex: item.routeSegmentIndex,
          });
          return;
        }

        interactions.onRouteSequenceSelect?.({
          routeNumber,
          routeSegmentIndex: item.routeSegmentIndex,
        });
      }}
      onMouseEnter={() => {
        interactions.onRouteSegmentHoverStart?.(item.routeSegmentIndex, hoverRouteSegmentIndexes);
      }}
      onMouseLeave={() => {
        interactions.onRouteHoverEnd?.();
      }}
      data-route-segment-index={item.routeSegmentIndex}
      {...elementProps}
    >
      <div className="grid gap-1.25 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center lg:gap-2">
        <RouteSequenceStepLead hideStartLabel={isGroupMember} indicators={routeStepIndicators} startLabel={stepStartLabel}>
          {item?.isEndOfDayLoadSourcePreload || item?.isEndOfDayLoad
            ? <EndOfDayLoadSourceCornerRibbon />
            : item?.isInitialPreload
              ? <InitialPreloadCornerRibbon />
              : null}
          <span className="relative inline-flex h-6.5 w-6.5 overflow-visible">
            <RouteStopAddressHoverCard
              title={stepSequenceLabel ? `${stepStartLabel ? `${stepStartLabel} · ` : ""}Étape ${stepSequenceLabel}` : stepStartLabel || "Étape"}
              eyebrow={destinationTypeLabel}
              label={destinationLabel}
              address={destinationAddress}
              accentColor={destinationAccentColor}
            >
              <span
                className={joinClasses([
                  "inline-flex cursor-help h-6.5 w-6.5 items-center justify-center whitespace-nowrap rounded-full border border-[rgba(0,0,0,0.42)] font-black shadow-[0_8px_16px_rgba(10,32,37,0.16)]",
                  // Plage « 28-30 » d'un Chargé PM : plus petite pour tenir dans le rond.
                  String(stepSequenceLabel || "").length > 3 ? "text-[0.5rem] tracking-[-0.03em]" : "text-[0.66rem]",
                  leftSequenceBadgeTextClassName,
                ])}
                style={{ backgroundColor: leftSequenceBadgeColor }}
              >
                {stepSequenceLabel || item.routeSegmentIndex}
              </span>
            </RouteStopAddressHoverCard>
          </span>
        </RouteSequenceStepLead>

        <div className={joinClasses(["grid min-w-0 gap-1 lg:items-center lg:gap-2.5", ROUTE_SEQUENCE_DETAILS_GRID_CLASS_NAME, destinationClassName])}>
          <div className="flex min-w-0 items-center gap-1.5">
            <RouteStopAddressHoverCard
              title={destinationTypeLabel}
              eyebrow="Adresse de l'opération"
              label={destinationLabel}
              address={destinationAddress}
              accentColor={destinationAccentColor}
            >
              <span
                className="inline-flex"
                role="button"
                tabIndex={0}
                aria-label={`Afficher l'adresse de ${destinationLabel}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.currentTarget.focus();
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  event.currentTarget.focus();
                }}
              >
                <span
                  className={joinClasses([
                    "inline-flex h-7.5 w-7.5 cursor-help flex-none items-center justify-center overflow-hidden p-0 transition-transform duration-300 group-hover/segment:-translate-y-0.5",
                    DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
                    stopIconClassNames[destinationStopType] || stopIconClassNames.delivery,
                  ])}
                  aria-hidden="true"
                >
                  {renderRouteStopIcon(destinationStopType)}
                </span>
              </span>
            </RouteStopAddressHoverCard>

            <div className="grid min-w-0 gap-px">
              <strong className="truncate text-[0.76rem] leading-tight text-text">{destinationLabel}</strong>
              {hasSupplementaryContent ? (
                <RouteSequenceSupplementaryMeta
                  indicators={visibleRouteContentIndicators}
                  summaryLabel={!shouldHideSupplementaryText ? inlineRouteSummaryLabel : ""}
                  addressLabel={!shouldHideSupplementaryText && !inlineRouteSummaryLabel ? destinationAddress : ""}
                />
              ) : null}
            </div>
          </div>

          {!item?.isTerminalRouteMarker ? (
            <RouteJourneyConnector
              className="lg:justify-center"
              orderLabel={item?.isPmOperationalLoading && item.endOfDayLoadOrderIds?.length > 1
                // Chargé PM : toutes ses commandes, comme un groupe du même site.
                ? getGroupedRouteConnectorOrderLabel(item.endOfDayLoadOrderIds.map((orderLabel) => ({ orderLabel })))
                : item?.operationOrderId || item?.orderLabel}
              stopType={destinationStopType}
            />
          ) : <div className="hidden lg:block" aria-hidden="true" />}

          <RouteMetaChipRow
            chips={displayChips}
            chipColumnClassNameMap={ROUTE_SEQUENCE_CHIP_COLUMN_CLASS_NAME_MAP}
            className="lg:flex-nowrap lg:justify-end"
            gridClassName={ROUTE_SEQUENCE_CHIP_GRID_CLASS_NAME}
            renderChipContent={buildRouteSequenceChipRenderer(totalCompartments)}
          />
        </div>
      </div>
    </li>
  );
}

export default function RouteSequencePanel({ highlightedRouteSegmentIndex = null, interactions = {}, model = {}, hovercardContext = null }) {
  const items = Array.isArray(model.items) ? model.items : [];
  const activeDispatchDate = typeof model?.dispatchDate === "string" && model.dispatchDate.trim()
    ? model.dispatchDate.trim()
    : readCurrentDispatchDate() || "";
  const [expandedGroupKeys, setExpandedGroupKeys] = React.useState([]);
  const [segmentComparisonByKey, setSegmentComparisonByKey] = React.useState(() => new Map());
  React.useEffect(() => {
    if (hovercardContext?.here_time_aware_routing_enabled !== true) {
      setSegmentComparisonByKey((currentMap) => (currentMap.size ? new Map() : currentMap));
      return undefined;
    }

    const requestTargets = [];
    const seenKeys = new Set();

    items.forEach((item) => {
      const comparisonKey = buildFleetSegmentComparisonCacheKey({
        dispatchDate: activeDispatchDate,
        routeSegmentIndex: item?.routeSegmentIndex,
        tripId: item?.tripId,
      });

      if (!comparisonKey || seenKeys.has(comparisonKey)) {
        return;
      }

      seenKeys.add(comparisonKey);
      requestTargets.push({
        comparisonKey,
        routeSegmentIndex: item?.routeSegmentIndex,
        tripId: item?.tripId,
      });
    });

    const pendingTargets = requestTargets.filter(({ comparisonKey }) => !segmentComparisonByKey.has(comparisonKey));
    if (!pendingTargets.length) {
      return undefined;
    }

    let isDisposed = false;
    const abortControllers = [];

    Promise.all(
      pendingTargets.map(async ({ comparisonKey, routeSegmentIndex, tripId }) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        try {
          const payload = await fetchRouteSequenceSegmentComparison({
            dispatchDate: activeDispatchDate,
            routeSegmentIndex,
            signal: abortController.signal,
            tripId,
          });
          return { comparisonKey, payload };
        } catch {
          return null;
        }
      }),
    ).then((entries) => {
      if (isDisposed) {
        return;
      }

      const resolvedEntries = entries.filter(Boolean);
      if (!resolvedEntries.length) {
        return;
      }

      setSegmentComparisonByKey((currentMap) => {
        const nextMap = new Map(currentMap);
        let didChange = false;

        resolvedEntries.forEach(({ comparisonKey, payload }) => {
          const currentPayload = nextMap.get(comparisonKey);
          const currentDurationMinutes = Number(currentPayload?.current_duration_minutes);
          const nextDurationMinutes = Number(payload?.current_duration_minutes);
          const payloadChanged = currentPayload?.status !== payload?.status
            || currentPayload?.here_time_aware_routing_enabled !== payload?.here_time_aware_routing_enabled
            || currentPayload?.comparison_duration_minutes !== payload?.comparison_duration_minutes
            || currentDurationMinutes !== nextDurationMinutes;

          if (!payloadChanged) {
            return;
          }

          nextMap.set(comparisonKey, payload);
          didChange = true;
        });

        return didChange ? nextMap : currentMap;
      });
    });

    return () => {
      isDisposed = true;
      abortControllers.forEach((abortController) => {
        abortController.abort();
      });
    };
  }, [activeDispatchDate, hovercardContext?.here_time_aware_routing_enabled, items]);
  const displayItems = React.useMemo(
    () => buildFleetSequenceDisplayItems(
      buildFleetDisplayedSegmentTimings(
        applyFleetSegmentComparisonDurations(items, segmentComparisonByKey, hovercardContext, activeDispatchDate),
        hovercardContext,
      ),
    ),
    [activeDispatchDate, hovercardContext?.here_time_aware_routing_enabled, items, segmentComparisonByKey],
  );
  const sequenceDisplayOverrides = React.useMemo(
    () => buildSameSiteSequenceDisplayOverrides(displayItems),
    [displayItems],
  );
  const renderItems = React.useMemo(
    () => buildRouteSequenceRenderItems(displayItems, { expandedGroupKeys }),
    [displayItems, expandedGroupKeys],
  );
  const routeSequenceOpeningVisibilityByItemKey = React.useMemo(
    () => buildRouteSequenceOpeningVisibilityByItemKey(renderItems),
    [renderItems],
  );
  const routeSequenceCompartmentOccupancyByItem = React.useMemo(
    () => buildRouteSequenceCompartmentOccupancyMap(renderItems, model?.totalCompartments || 0),
    [model?.totalCompartments, renderItems],
  );
  const presentChipKinds = React.useMemo(
    () => new Set(displayItems.flatMap((item, itemIndex) => (
      buildRouteSequenceDisplayChips(item, sequenceDisplayOverrides, itemIndex, displayItems, hovercardContext).map((chip) => chip?.kind)
    ))),
    [displayItems, hovercardContext, sequenceDisplayOverrides],
  );
  const chipColumnsStyle = {
    "--route-chip-columns": ROUTE_SEQUENCE_CHIP_COLUMNS
      .map((column) => (!column.optional || presentChipKinds.has(column.kind) ? column.width : "0px"))
      .join(" "),
  };
  const totalCompartments = model?.totalCompartments || 0;

  const toggleExpandedGroup = React.useCallback((groupKey) => {
    if (!groupKey) {
      return;
    }

    setExpandedGroupKeys((currentKeys) => (
      currentKeys.includes(groupKey)
        ? currentKeys.filter((currentKey) => currentKey !== groupKey)
        : [...currentKeys, groupKey]
    ));
  }, []);

  React.useEffect(() => {
    setExpandedGroupKeys((currentKeys) => {
      if (!currentKeys.length) {
        return currentKeys;
      }

      const validGroupKeys = new Set(
        displayItems
          .map((item) => (typeof item?.sameSiteGroupKey === "string" ? item.sameSiteGroupKey.trim() : ""))
          .filter(Boolean),
      );
      const nextKeys = currentKeys.filter((groupKey) => validGroupKeys.has(groupKey));

      return nextKeys.length === currentKeys.length ? currentKeys : nextKeys;
    });
  }, [displayItems]);

  return (
    <PanelShell
      title={model.truckName ? `Séquence route · ${model.truckName}` : "Séquence route"}
      description={model.driverName}
      titleClassName="!text-[clamp(1.02rem,1.15vw,1.28rem)] !leading-[1.08] !tracking-[-0.02em] !text-white"
      descriptionClassName="!text-[0.68rem] !leading-[1.2] !text-[#eee9e3]"
      copyClassName="!gap-0"
      headerClassName={`mb-1 lg:items-center ${DISPATCH_DARK_HEADER_CLASS_NAME}`}
      actions={<span className={DISPATCH_DARK_HEADER_CHIP_CLASS_NAME} data-route-target-stop-summary="true">{model.countLabel}</span>}
      className={`gap-0! border border-[rgba(148,163,184,0.16)] bg-white px-2 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.06)] rounded-[4px]`}
      bodyClassName="grid gap-0.75"
    >
      {model.showEmptyState ? (
        <EmptyState
          tone="subtle"
          title={model.emptyTitle}
          message={model.emptyMessage}
        />
      ) : (
        <>
        <div style={chipColumnsStyle}>
          <RouteSequenceColumnHeader presentChipKinds={presentChipKinds} />
        </div>
        <ol className="m-0 grid list-none gap-0.75 p-0" data-route-target-stop-list="true" style={chipColumnsStyle}>
          {renderItems.map((item) => {
            if (item.kind === "group") {
              const groupItem = item;
              const highlightSegments = [
                ...(Array.isArray(groupItem.memberSegments) ? groupItem.memberSegments : []),
                groupItem.mergedNextSegment || null,
              ].filter(Boolean);
              const isHighlighted = highlightSegments.some((memberSegment) => {
                const itemRouteSegmentIndex = Number.parseInt(memberSegment?.routeSegmentIndex, 10);
                return Number.isInteger(itemRouteSegmentIndex) && itemRouteSegmentIndex === highlightedRouteSegmentIndex;
              });

              return (
                <GroupedRouteSegmentRow
                  key={groupItem.key}
                  displayOverrides={sequenceDisplayOverrides}
                  groupItem={groupItem}
                  hovercardContext={hovercardContext}
                  interactions={interactions}
                  isExpanded={groupItem.isExpanded === true}
                  isHighlighted={isHighlighted}
                  occupancyLabel={routeSequenceCompartmentOccupancyByItem.get(groupItem.key) || ""}
                  onToggleExpand={toggleExpandedGroup}
                  sequenceItems={displayItems}
                  showOpeningTime={shouldDisplayRouteSequenceOpeningForItem(groupItem.key, routeSequenceOpeningVisibilityByItemKey)}
                  totalCompartments={totalCompartments}
                />
              );
            }

            const routeItem = item.kind === "group-member" ? item.segment : item.segment || item;
            const itemRouteSegmentIndex = Number.parseInt(routeItem?.routeSegmentIndex, 10);

            return (
              <RouteSegmentRow
                key={item.key}
                className={item.kind === "group-member" ? "lg:ml-3" : ""}
                displayOverrides={sequenceDisplayOverrides}
                destinationClassName={item.kind === "group-member" ? "lg:-ml-3" : ""}
                hovercardContext={hovercardContext}
                isGroupMember={item.kind === "group-member"}
                item={{
                  ...routeItem,
                  isHighlighted: Number.isInteger(itemRouteSegmentIndex) && itemRouteSegmentIndex === highlightedRouteSegmentIndex,
                }}
                itemIndex={Math.max(displayItems.indexOf(routeItem), 0)}
                interactions={interactions}
                occupancyLabel={routeSequenceCompartmentOccupancyByItem.get(item.key) || ""}
                sequenceItems={displayItems}
                showOpeningTime={shouldDisplayRouteSequenceOpeningForItem(item.key, routeSequenceOpeningVisibilityByItemKey)}
                totalCompartments={totalCompartments}
              />
            );
          })}
        </ol>
        </>
      )}
    </PanelShell>
  );
}
